// Markers drawn on a reference image: points (R), lines (L), areas (A) and angles (D),
// normalized 0..1 of the reference. Mirrors 3d-backend/services/spatial-vision/markers.js,
// which validates again on save; here the client only numbers, edits and removes.
import { shapeWord } from './marksGeometry.js';

export const MARKER_PREFIX = { point: 'R', line: 'L', area: 'A', angle: 'D' };

const empty = () => ({
  label: '',
  type: { mode: 'free', value: '' },
  nominal: null,
  tolerance: null,
});

export function nextMarkerId(markers, kind) {
  const used = new Set((markers || []).map(marker => marker.id));
  let number = 1;
  while (used.has(`${MARKER_PREFIX[kind]}${number}`)) number += 1;
  return `${MARKER_PREFIX[kind]}${number}`;
}

// The canvas emits one of three shapes; which marker it becomes follows from the shape,
// not from the button that started the gesture.
export function markerKindOf(shape) {
  if (shape?.x1 !== undefined) return 'line';
  if (shape?.type) return 'area';
  return 'point';
}

// A line's ends and a circle's rim follow the edges in the photo unless the operator
// turns it off: a line drawn across a feature is meant to measure that feature.
export const canSnap = (kind, shape) =>
  kind === 'line' || (kind === 'area' && shape?.type === 'circle');

// An area is named by the run unless the operator writes a type; nothing asks.
export function markerFromShape(kind, shape, markers) {
  return {
    id: nextMarkerId(markers, kind),
    kind,
    shape,
    ...empty(),
    ...(kind === 'area' ? { type: { mode: 'auto', value: '' } } : {}),
    ...(canSnap(kind, shape) ? { snap: true } : {}),
  };
}

export function lineIds(markers) {
  return (markers || [])
    .filter(marker => marker.kind === 'line')
    .map(marker => marker.id);
}

export function pointIds(markers) {
  return (markers || [])
    .filter(marker => marker.kind === 'point')
    .map(marker => marker.id);
}

export function angleMarker(markers, lineA, lineB) {
  const lines = lineIds(markers);
  if (lineA === lineB || !lines.includes(lineA) || !lines.includes(lineB)) {
    return null;
  }
  return {
    id: nextMarkerId(markers, 'angle'),
    kind: 'angle',
    lines: [lineA, lineB],
    ...empty(),
  };
}

// An angle without one of its lines means nothing, so it goes with the line.
export function withoutMarker(markers, id) {
  return (markers || []).filter(
    marker =>
      marker.id !== id &&
      !(marker.kind === 'angle' && marker.lines.includes(id)),
  );
}

export function calibrationWithout(calibration, id) {
  if (!calibration) return null;
  return calibration.r1 === id || calibration.r2 === id ? null : calibration;
}

export function markerUnit(kind) {
  if (kind === 'angle') return 'deg';
  if (kind === 'line' || kind === 'area') return 'mm';
  return null;
}

export function draftsFromImages(images) {
  const referenceMarkerDrafts = {};
  const referenceCalibrationDrafts = {};
  (images || []).forEach(image => {
    const id = String(image.id);
    referenceMarkerDrafts[id] = Array.isArray(image.markers)
      ? image.markers
      : [];
    referenceCalibrationDrafts[id] = image.calibration || null;
  });
  return { referenceMarkerDrafts, referenceCalibrationDrafts };
}

export function markerSummary(marker) {
  const parts = [marker.id];
  if (marker.label) parts.push(marker.label);
  if (marker.kind === 'point') parts.push('Label');
  else if (marker.kind === 'line') parts.push('Line');
  else if (marker.kind === 'angle')
    parts.push(`Angle ${marker.lines[0]}–${marker.lines[1]}`);
  else parts.push(shapeWord(marker.shape));
  return parts.join(' · ');
}

// One row per reference the editor shows: saved images first, then files picked but
// not yet uploaded. The key is the image id or the file's pending key, and it is the
// key marker drafts live under, so drawing works before the first save.
// Saved images in the order they were added: the database hands them back in id order,
// and the operator's words point at these numbers ("references 1-3 are defective").
const addedOrder = (a, b) =>
  new Date(a.uploadedAt || 0) - new Date(b.uploadedAt || 0) ||
  String(a.id).localeCompare(String(b.id));

export function referenceSlots(existing, pending) {
  const saved = [...(existing || [])].sort(addedOrder).map((image, index) => ({
    key: String(image.id),
    name: `Reference Image ${index + 1}`,
    src: image.storageUrl,
    pending: false,
    image,
  }));
  const picked = (pending || []).map((entry, index) => ({
    key: entry.key,
    name: `Reference Image ${saved.length + index + 1}`,
    src: entry.preview,
    pending: true,
    index,
    file: entry.file,
  }));
  return [...saved, ...picked];
}

export function hasMarkerDraft(markers, calibration) {
  return Boolean((markers || []).length || calibration);
}
